<template>
  <div>
    <div v-if="error" class="uk-alert-danger" uk-alert="animation: true;" @hide="statusHide">
      <a class="uk-alert-close" uk-close></a>
      <p>{{ error }}</p>
    </div>
    <div class="uk-margin-bottom">
      <form
        v-for="(invitee, inviteeIdx) in form.invitees"
        :key="inviteeIdx"
        class="uk-flex uk-flex-row uk-flex-top uk-flex-between uk-margin-bottom"
      >
        <div class="uk-flex uk-flex-row">
          <template v-for="(f, fieldIdx) in Object.keys(formFields)" :key="f">
            <template
              v-if="
                !formFields[f].uiModeHidden ||
                (formFields[f].uiModeHidden && uiMode !== formFields[f].uiModeHidden)
              "
            >
              <span v-if="formFields[f].type === 'label'" style="width: 1%"
                >{{ inviteeIdx + 1 }}
              </span>
              <form-group
                v-else
                :class="{
                  error: v$.form.invitees[inviteeIdx]?.[f]?.$error,
                  'input-margin-left': fieldIdx > 0,
                }"
                :validator="v$.form.invitees[inviteeIdx]?.[f]"
              >
                <span class="uk-inline uk-flex-stretch">
                  <span
                    v-if="formFields[f].icon"
                    class="uk-form-icon"
                    :uk-icon="`icon: ${formFields[f].icon}`"
                  ></span>
                  <input
                    class="uk-input"
                    :type="formFields[f].type"
                    :placeholder="formFields[f].placeholder"
                    v-model="invitee[f]"
                    required
                    @input="v$.form.invitees[inviteeIdx]?.[f]?.$touch()"
                  />
                </span>
              </form-group>
            </template>
          </template>

          <vue-tel-input
            class="uk-width-1-1 input-margin-left"
            v-model="invitee.phone"
            mode="international"
            default-country="US"
            @validate="validatePhoneNumber"
            :valid-characters-only="true"
            :input-options="{
              placeholder: 'Mobile number',
            }"
            :style-classes="[
              'vue-tel-input-custom',
              { 'vue-tel-input-error': !isPhoneNumberValid },
            ]"
          />
        </div>

        <button
          v-if="form.invitees.length !== 1"
          type="button"
          class="btn-remove uk-button uk-button-default uk-border-circle uk-padding-remove input-margin-left"
          @click="removeInvitee(inviteeIdx)"
        >
          <i uk-icon="icon: ban; ratio:1.5" uk-tooltip="Remove Invitee"></i>
        </button>
      </form>
    </div>
    <hr />
    <div class="uk-margin-bottom">
      <div class="uk-flex" style="position: relative">
        <button
          class="uk-button uk-button-default uk-margin-bottom"
          type="button"
          style="padding-right: 16px; padding-left: 16px"
          @click="addInvitee"
        >
          <i uk-icon="plus"></i>
          {{ customAddText }}
        </button>
        <button
          class="uk-flex uk-flex-middle uk-margin-small-left uk-margin-bottom uk-button uk-button-default"
          type="button"
          style="cursor: pointer; padding-right: 8px; padding-left: 8px"
          @click="importContacts"
        >
          <img :src="`/icons/black/icon-black-user.svg`" width="17" />
          <span class="uk-text-small padding-text" style="">Import Contacts</span>
        </button>
        <button
          class="uk-flex uk-flex-middle uk-margin-small-left uk-button uk-margin-bottom uk-button-default"
          style="cursor: pointer; padding-right: 8px; padding-left: 8px"
          type="button"
          @click="loadRoster"
        >
          <img :src="`/icons/black/icon-black-users.svg`" width="17" />
          <span class="uk-text-small padding-text" style="">Import Roster</span>
        </button>
        <input
          v-if="form.invitees.length !== 1 && modalView"
          @blur="setTeamName"
          placeholder="New Roster Name"
          style="
            margin-left: 14px;
            margin-top: 0px !important;
            position: absolute;
            right: 0px;
            margin-right: 40px;
          "
          v-model="teamName"
          class="uk-input uk-width-1-5 uk-margin-small-top"
          type="input"
        />
      </div>
    </div>
    <div v-if="modalView" class="my-contacts" :class="{ 'my-contacts-display': myContacts }">
      <MainContentPanel
        title="MyContacts"
        UkButtonIcon="users"
        buttonText="Import"
        tooltip=""
        :buttonAction="addContacts"
        :medium="true"
        padding="small"
        class="uk-border-rounded"
        :closeButton="true"
        @closeImportModal="closeImportModal"
      >
        <PrimeDataTable
          ref="dataTable"
          :tableConfig="tablePeopleConfig"
          :tableOptions="tablePeopleOptions"
          :pagination="false"
          :scrollHeight="`300px`"
          :loadDataFunction="loadContacts"
          @selectedRows="data => (selectedContacts = data)"
          @selectedColumns="tableToggleData"
        />
      </MainContentPanel>
    </div>
    <div class="my-contacts" :class="{ 'my-team-display': myTeam }">
      <MainContentPanel
        :key="updated"
        title="MyRosters"
        UkButtonIcon="users"
        buttonText="Import"
        tooltip=""
        :buttonAction="addTeamContacts"
        :medium="true"
        padding="small"
        class="uk-border-rounded"
        :closeButton="true"
        @closeImportModal="closeImportModal"
      >
        <PrimeDataTable
          ref="dataTable"
          :pagination="false"
          :scrollHeight="`300px`"
          :tableConfig="tableTeamConfig"
          :tableOptions="tableTeamOptions"
          :loadDataFunction="loadTeams"
          @selectedRows="data => (selectedTeams = data)"
          @selectedColumns="tableToggleData"
        />
      </MainContentPanel>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent, reactive, computed } from 'vue';
import LoadingStatus from '@/assets/js/enum/LoadingStatus';
import { VueTelInput } from 'vue-tel-input';
import 'vue-tel-input/vue-tel-input.css';
import { mapGetters, mapState } from 'vuex';
import { required, email, helpers } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { EventBus } from '@/assets/js/event-bus';
import MainContentPanel from '@/components/display/general/MainContentPanel';
import { mapActions } from 'vuex';

export default {
  name: 'TeamLeadInvitationForm',
  components: {
    VueTelInput,
    MainContentPanel,
    PrimeDataTable: defineAsyncComponent(() => import('@/components/table/PrimeDataTable.vue')),
  },
  setup() {
    const form = reactive({
      invitees: [
        {
          firstname: '',
          lastname: '',
          email: '',
          phone: '',
          order: 1,
        },
      ],
      teamName: '',
      challengeUrl: '',
    });

    const rules = computed(() => ({
      form: {
        invitees: {
          $each: helpers.forEach({
            firstname: { required },
            lastname: { required },
            email: { required, email },
            phone: {},
            order: {},
          }),
        },
      },
    }));

    const v$ = useVuelidate(rules, { form });

    return { form, v$: v$.value };
  },
  props: {
    data: Object,
    teamID: String,
    invitation: Object,
    customAddText: String,
    urlCheckBox: {
      type: Boolean,
      default: false,
    },
    modalView: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['success'],
  data() {
    return {
      formFields: {
        order: {
          type: 'label',
        },
        firstname: {
          type: 'text',
          icon: 'user',
          placeholder: 'First Name',
        },
        lastname: {
          type: 'text',
          icon: 'user',
          placeholder: 'Last Name',
        },
        email: {
          type: 'email',
          icon: 'mail',
          placeholder: 'Email',
        },
        phone: {
          type: 'phone',
          icon: 'lock',
          placeholder: 'phone',
          uiModeHidden: 'new',
        },
      },
      savingStatus: '',
      error: '',
      isPhoneNumberValid: true,
      myContacts: true,
      myTeam: true,
      teamName: '',
      hasTeamName: true,
      contacts: [],
      selectedContacts: [],
      selectedTeams: [],
      updated: 0,
      generateLink: false,
      challengeUrl: '',
      // Team tags: Deprecated
      // tagsOptions: [],
      // teamTagsOptions: []
    };
  },
  watch: {
    myContacts(value) {
      EventBus.emit('import.contacts.show', value);
    },
    myTeam(value) {
      EventBus.emit('import.team.show', value);
    },
    invitation() {
      if (this.invitation?.invitees?.length)
        this.form.invitees = this.invitation.invitees;
    },
    randomLink(value) {
      this.form.challengeUrl = value;
    },
    teamName(value) {
      if (value) {
        this.hasTeamName = false;
      } else {
        this.hasTeamName = true;
      }
    },
  },
  computed: {
    EventBus() {
      return EventBus;
    },
    LoadingStatus() {
      return LoadingStatus;
    },
    ...mapState(['idToken', 'userData', 'currentTeamMemberID']),
    ...mapGetters(['tableToggleData', 'sortAndFilter']),
    uiMode() {
      return this.data ? 'details' : 'new';
    },
    saveButtonText() {
      return this.uiMode === 'details' ? 'Save Changes' : 'Send Request to Join';
    },
    tablePeopleOptions() {
      return {
        // globalFilterFields: ['firstname'],
        selectable: true,
        rowSelectable: true,
        placeHolderData: true,
        tableName: 'tablePeopleInvite',
      };
    },

    tablePeopleConfig() {
      const tableConfig = [
        {
          column: 'First Name',
          field: 'firstname',
          sortable: true,
          model: row => {
            return row.firstname;
          },
          index: 1,
        },
        {
          column: 'Lastname',
          field: 'lastname',
          sortable: true,
          model: row => {
            return row.lastname;
          },
          index: 2,
        },
        // Team tags: Deprecated
        // {
        //   column: 'Tags',
        //   field: 'tags',
        //   filter: {
        //     type: 'select',
        //     options: this.tagsOptions,
        //   },
        // }
      ];
      return tableConfig;
    },

    tableTeamOptions() {
      return {
        // globalFilterFields: ['name'],
        selectable: true,
        rowSelectable: true,
        tableName: 'tableTeamInvite',
      };
    },

    tableTeamConfig() {
      const tableConfig = [
        {
          column: 'Name',
          field: 'name',
          sortable: true,
        },
        // Team tags: Deprecated
        // {
        //   column: 'Tags',
        //   field: 'tags',
        //   filter: {
        //     type: 'select',
        //     options: this.teamTagsOptions,
        //   },
        // },
      ];
      return tableConfig;
    },
  },

  mounted() {
    EventBus.on('invitation.teamLead.invitees.new', this.submitForm);
    EventBus.on('dblClick.add.data', this.dblClickDataTransfer);
    if (this.invitation?.invitees?.length)
      this.form.invitees = this.invitation.invitees;
  },
  beforeUnmount() {
    EventBus.off('invitation.teamLead.invitees.new', this.submitForm);
    EventBus.off('dblClick.add.data', this.dblClickDataTransfer);
  },
  methods: {
    ...mapActions(['loadMyContacts', 'loadMyTeams', 'getTeamList', 'addMyContacts', 'addMyTeam']),
    addInvitee() {
      if (this.form.invitees.length >= this.maxInvitees)
        alert('Maximum amount of invitees reached');
      this.form.invitees.splice(this.form.invitees.length, 0, {
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        order: this.form.invitees.length + 1,
      });
    },
    removeInvitee(idx) {
      this.form.invitees.splice(idx, 1);
    },

    validatePhoneNumber(phoneObject) {
      // vue-tel-input passes { valid: boolean, country: object, ... }
      this.isPhoneNumberValid = phoneObject.valid;
    },

    submitForm({ success, failed }) {
      if (!this.generateLink) {
        this.v$.form.$touch();
        if (this.v$.form.$pending || this.v$.form.$error || !this.isPhoneNumberValid) {
          if (failed) failed();
        } else {
          EventBus.emit('invitation.invitee.avilable', true);
          if (success) success(this.form);
        }
      } else {
        if (this.form.invitees[0].email !== '') {
          EventBus.emit('invitation.invitee.avilable', true);
        } else {
          EventBus.emit('invitation.invitee.avilable', false);
        }
        success(this.form);
      }
    },

    clearForm() {
      this.clearError();
      this.form.invitees = [
        {
          firstname: '',
          lastname: '',
          email: '',
          phone: '',
          order: 1,
        },
      ];
      this.v$.form.$reset();
    },
    importContacts() {
      if (this.myTeam === false) {
        this.myTeam = true;
        this.myContacts = false;
      } else this.myContacts = false;
    },
    closeImportModal() {
      this.myTeam = true;
      this.myContacts = true;
    },
    async loadContacts({ sortColumn, sortDirection, filters }) {
      let res = {
        results: this.contacts,
        page: 0,
        resultsPerPage: 0,
        totalCount: this.contacts.length,
      };
      if (!this.contacts.length) {
        res = await this.loadMyContacts({ sortColumn, sortDirection, filters });
        this.contacts = res.results;
      }
      // Team tags: Deprecated
      // this.tagsOptions = []
      // if(filters?.archived?.value) {
      //   res.results.map((item) => {
      //     this.tagsOptions = [...this.tagsOptions, ...item.tags.filter((tag) => !this.tagsOptions.includes(tag))]
      //   })
      // } else {
      //   res.results.map((item) => {
      //     if(!item.archived) this.tagsOptions = [...this.tagsOptions, ...item.tags.filter((tag) => !this.tagsOptions.includes(tag))]
      //   })
      // }
      res.results = this.sortAndFilter({ data: res.results, sortColumn, sortDirection, filters });
      return res;
    },

    async loadTeams({ sortColumn, sortDirection, filters }) {
      const res = await this.getTeamList({ sortColumn, sortDirection, filters });
      // Team tags: Deprecated
      // this.teamTagsOptions = []
      // if(filters?.archived?.value) {
      //   res.results.map((item) => {
      //     this.teamTagsOptions = [...this.teamTagsOptions, ...item.tags.filter((tag) => !this.teamTagsOptions.includes(tag))]
      //   })
      // } else {
      //   res.results.map((item) => {
      //     if(!item.archived) this.teamTagsOptions = [...this.teamTagsOptions, ...item.tags.filter((tag) => !this.teamTagsOptions.includes(tag))]
      //   })
      // }
      return res;
    },
    dblClickDataTransfer(data) {
      if (data.name !== undefined) {
        setTimeout(() => {
          this.addTeamContacts(true);
        }, 180);
      } else {
        setTimeout(() => {
          this.addContacts(true);
        }, 180);
      }
    },
    addContacts(val) {
      let contacts = [...this.form.invitees.filter(item => item.email)];
      if (this.selectedContacts.length !== 0) {
        this.selectedContacts.forEach(item => {
          if (contacts.filter(data => data.email === item.email).length === 0) {
            contacts.push({
              firstname: item.firstname,
              lastname: item.lastname,
              email: item.email,
              phone: item.phonenumber,
              order: contacts.length + 1,
            });
          } else {
            if (contacts.length === 1) {
              contacts = [
                {
                  firstname: '',
                  lastname: '',
                  email: '',
                  phone: '',
                  order: 1,
                },
              ];
            } else {
              contacts = contacts.filter(data => data.email !== item.email);
            }
          }
        });
        EventBus.emit('selected.field.empty');
        this.form.invitees = contacts;
        if (!val) {
          this.myContacts = true;
        }
      }
    },
    setTeamName() {
      this.form.teamName = this.teamName;
    },
    loadRoster() {
      if (this.myContacts === false) {
        this.myContacts = true;
        this.myTeam = false;
      } else this.myTeam = false;
    },
    addTeamContacts(val) {
      let teamContacts = [...this.form.invitees.filter(item => item.email)];
      this.selectedTeams.map(item => {
        item.contacts.forEach(itemData => {
          if (teamContacts.filter(data => data.email === itemData.email).length === 0) {
            teamContacts.push({
              firstname: itemData.firstname,
              lastname: itemData.lastname,
              email: itemData.email,
              phone: itemData.phonenumber,
              order: teamContacts.length + 1,
            });
          } else {
            if (teamContacts.length === 1) {
              teamContacts = [
                {
                  firstname: '',
                  lastname: '',
                  email: '',
                  phone: '',
                  order: 1,
                },
              ];
            } else {
              teamContacts = teamContacts.filter(data => data.email !== itemData.email);
            }
          }
        });
      });
      EventBus.emit('selected.field.empty');
      this.form.invitees = teamContacts;
      if (!val) {
        this.myTeam = true;
      }
    },
    async saveTeam() {
      let data = [];
      let contactsToBeCreated = [];
      for (let i = 0; i < this.form.invitees.length; i++) {
        const element = this.form.invitees[i];
        let contactExists = this.contacts.filter(contact => contact.email === element.email);
        if (contactExists.length) {
          data.push(contactExists[0]._id);
        } else if (element.email) {
          contactsToBeCreated.push({
            firstname: element.firstname,
            lastname: element.lastname,
            email: element.email,
            phonenumber: element.phone,
            archived: false,
          });
        }
      }
      let contactRes = await this.addMyContacts({ contacts: contactsToBeCreated });
      contactsToBeCreated = contactsToBeCreated.map(contact => contact.email);
      contactRes[0].map(contact => {
        if (contactsToBeCreated.includes(contact.email)) {
          data.push(contact._id);
        }
      });
      await this.addMyTeam({
        name: this.teamName,
        // Team tags: Deprecated
        // tags: [],
        contacts: data,
        archived: false,
      });
      this.teamName = '';
      this.updated++;
    },
  },
};
</script>

<style lang="sass" scoped>
.my-contacts
  position: absolute
  transform: translate(42vw)
  top: 0
  width: 65%
  @media only screen and (max-width: 1670px)
    transform: translate(43vw)
  @media only screen and (max-width: 1550px)
    transform: translate(43vw)
  @media only screen and (max-width: 1500px)
    transform: translate(45vw)
  @media only screen and (max-width: 1450px)
    transform: translate(46vw)
    width: 70%
  @media only screen and (max-width: 1400px)
    transform: translate(46vw)
    width: 70%
  @media only screen and (max-width: 1220px)
    transform: translate(47vw)
.my-team-display
  display: none
.my-contacts-display
  display: none
.error
  font-size: .60em
.input-margin-left
  margin-left: 13px !important
.btnDisable
  pointer-events: none
  opacity: 0.5
.padding-text
  padding-left: 10px
</style>
